import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eyebrow } from './UI';
import { ChevronDown } from 'lucide-react';
export const FAQ = () => {
  const faqs = [{
    q: 'Is this mask actually rated for airsoft and high-FPS play?',
    a: 'Yes. The BLACKOUT UNIT Ultimate Mask uses Lexan polycarbonate lenses rated ANSI Z87.1 — the industry standard for airsoft eye protection. Tested at direct BB impact up to 250+ FPS. For fields running 400+ FPS, standard 10-meter minimum engagement rules apply. The composite shell provides full-face coverage against fragmentation, debris, and impact.'
  }, {
    q: 'How do I swap the lenses?',
    a: 'Tool-free. Press the two release tabs on either side of the lens frame, slide the lens forward, remove. New lens pushes in and locks with an audible click. Average swap time: under 30 seconds in the field.'
  }, {
    q: 'Does it fit over eyeglasses?',
    a: 'The interior foam padding is designed to seat directly against your face. Many users with thin wire-frame glasses report a comfortable fit. Customers with thicker frames may find the Clear lens configuration provides the most interior clearance.'
  }, {
    q: 'What exactly does "lifetime warranty" cover?',
    a: 'The frame, composite shell, stainless steel skeleton, and strap hardware are warranted against structural defect under normal operational use — for life. Lenses carry a 1-year anti-fog coating and anti-scratch warranty. The warranty does not cover intentional destruction or acts of extreme force outside normal use (our F-250 test was our idea — not yours).'
  }, {
    q: 'Is this mask legal for regulated airsoft fields?',
    a: "The BLACKOUT UNIT meets ANSI Z87.1 standards — the baseline requirement at most regulated airsoft and paintball venues in the United States. We always recommend confirming with your specific field's safety officer prior to competition play."
  }, {
    q: "What's your return process?",
    a: '30 days. No questions. Contact us at support@blackoutunit.com or call +1 (800) 000-0000. We send a prepaid return label. Refunds processed within 3–5 business days of receipt.'
  }, {
    q: 'Do you offer bulk or wholesale for teams?',
    a: 'Yes. Email wholesale@blackoutunit.com with your organization, quantity needed, and intended application. Response within 24 hours with a custom quote and team discount structure.'
  }, {
    q: 'What makes this different from every other skull mask?',
    a: 'Material category. Every competitor uses ABS or PVC injection plastic — materials that crack, warp, and fail within a season of hard use. The BLACKOUT UNIT uses glass-fiber reinforced composite shell over a Grade 304 stainless steel skeleton. It is a fundamentally different product in the same visual category.'
  }];
  const half = Math.ceil(faqs.length / 2);
  const col1 = faqs.slice(0, half);
  const col2 = faqs.slice(half);
  const AccordionItem = ({
    q,
    a,
    index
  }: {
    q: string;
    a: string;
    index: number;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-50px'
    }} transition={{
      duration: 0.5,
      delay: index * 0.1
    }} className={`relative bg-dark-1/80 backdrop-blur-sm border transition-all duration-300 mb-4 overflow-hidden group ${isOpen ? 'border-acid-green shadow-[0_0_15px_rgba(200,255,0,0.1)]' : 'border-dark-3 hover:border-acid-green/50'}`} data-id="element-147">
        {/* Animated accent line */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${isOpen ? 'bg-acid-green' : 'bg-transparent group-hover:bg-acid-green/30'}`} data-id="element-148" />

        <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center p-6 pl-8 text-left" data-id="element-149">
          <div className="flex items-center gap-4" data-id="element-150">
            <span className={`font-barlow font-bold text-[10px] tracking-[3px] transition-colors duration-300 ${isOpen ? 'text-acid-green' : 'text-text-dimmed'}`} data-id="element-151">
              Q.{String(index + 1).padStart(2, '0')}
            </span>
            <span className={`font-bebas text-xl md:text-2xl pr-4 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-white/80 group-hover:text-white'}`} data-id="element-152">
              {q}
            </span>
          </div>
          <div className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-all duration-300 ${isOpen ? 'border-acid-green bg-acid-green text-black' : 'border-dark-3 text-acid-green group-hover:border-acid-green/50'}`} data-id="element-153">
            <ChevronDown size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-154" />
          </div>
        </button>
        <AnimatePresence data-id="element-155">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-156">
              <div className="p-6 pl-8 pt-0 font-inter text-sm md:text-base text-text-dimmed leading-relaxed border-t border-dark-3/30 mt-2" data-id="element-157">
                {a}
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.div>;
  };
  return <section className="relative py-[100px]" data-id="element-158">
      {/* Background Image with Dark Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-159">
        <img src="https://cdn.magicpatterns.com/uploads/nG8ULCaEp4SdxkWLmpe5za/hf_20260505_163744_f1440f2b-fb41-4cb5-8671-6935791fc173.png" alt="Blackout Unit Silver Mask" className="w-full h-full object-cover opacity-80" data-id="element-160" />
        {/* Animated gradient overlay for a more dynamic feel */}
        <div className="absolute inset-0 bg-gradient-to-b from-base-black via-base-black/40 to-base-black" data-id="element-161"></div>
        <div className="absolute inset-0 bg-tactical-grid opacity-20 pointer-events-none" data-id="element-162"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,rgba(200,255,0,0.05)_0%,transparent_70%)] pointer-events-none" data-id="element-163"></div>
      </div>

      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-164">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-165">
          <Eyebrow data-id="element-166">FIELD INTEL</Eyebrow>
          <h2 className="text-5xl md:text-[64px] leading-[0.9] mb-4" data-id="element-167">
            GOT QUESTIONS?{' '}
            <span className="text-acid-green" data-id="element-168">WE'VE GOT ANSWERS.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8" data-id="element-169">
          <div data-id="element-170">
            {col1.map((faq, i) => <AccordionItem key={i} index={i} {...faq} data-id="element-171" />)}
          </div>
          <div data-id="element-172">
            {col2.map((faq, i) => <AccordionItem key={i} index={i + col1.length} {...faq} data-id="element-173" />)}
          </div>
        </div>
      </div>
    </section>;
};